// deps
import { useState, useCallback, useEffect, useContext, useRef, useMemo } from "react";

// internal deps
import { useMountedRef, captureError } from "@swiggy-private/core-ui";
import { CreatePennyTransactionResponse } from "@swiggy-private/payment-types";

// contexts
import { JuspayContext } from "@payment-client/contexts/JuspayContext";
import { PaymentModesContext } from "@payment-client/contexts/PaymentModesContext";

// api
import { createPennyTransactionPublic } from "@payment-client/apis/publicPayment";

// hooks
import {
    CheckoutActionTypes,
    ConfigContext,
    ApiContext,
    CheckoutContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    PublicApiContext,
} from "@swiggy-private/checkout-core";

// interfaces
import { CreateTransactionData } from "@payment-client/interfaces/TxnApi";
import { getCreateTransactionParams } from "@payment-client/helpers/TransactionHelper";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { createPennyTransaction } from "@payment-client/apis/Payment";

interface CreateTransactionState {
    createTransactionInProgress: boolean;
    transactionData?: CreateTransactionData;
}

type useCreateTransactionReturnType = [
    CreateTransactionState,
    (data: NonNullable<CreateTransactionState["transactionData"]>) => void,
];

const useCreateTransaction = (): useCreateTransactionReturnType => {
    const isMountedRef = useMountedRef();
    const apiContextValue = useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const { paymentReturnUrl } = useContext(ConfigContext);
    const { isSdkAvailable: isJuspaySdkAvailable } = useContext(JuspayContext);
    const apiCallRef = useRef(false);
    const { setCheckoutState } = useContext(CheckoutContext);
    const { getAllCardsResponse } = useContext(PaymentModesContext);
    const [createTransactionState, setCreateTransactionState] = useState<CreateTransactionState>({
        createTransactionInProgress: false,
    });

    const isPublicApiEnabledForCreateTransaction = useMemo(() => {
        return isPublicApiEnabled(
            publicApiConfig || {},
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYMENT_APIS,
            CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.CREATE_PENNY_TRANSACTION,
        );
    }, [publicApiConfig]);
    const createTransaction = isPublicApiEnabledForCreateTransaction
        ? createPennyTransactionPublic
        : createPennyTransaction;

    const doCreateTransaction = useCallback(
        (transactionData: CreateTransactionData) => {
            if (isMountedRef.current && !createTransactionState.createTransactionInProgress) {
                setCreateTransactionState({ createTransactionInProgress: true, transactionData });
            }
        },
        [isMountedRef, createTransactionState.createTransactionInProgress],
    );

    const apiHost = apiContextValue.host.unwrap();

    const handleCreateTransactionData = useCallback(
        (transactionResponseData: CreatePennyTransactionResponse) => {
            const { transactionData } = createTransactionState;
            if (!isMountedRef.current || !transactionData) {
                return;
            }

            setCheckoutState({
                type: CheckoutActionTypes.PROCESS_TRANSACTION,
                payload: {
                    transactionReqData: transactionData,
                    transaction: transactionResponseData,
                    useJuspaySDK: isJuspaySdkAvailable,
                    merchantInfo: getAllCardsResponse?.merchantInfo,
                    meta: getAllCardsResponse?.meta.tokenizationMeta,
                },
            });
        },
        [createTransactionState, getAllCardsResponse, isJuspaySdkAvailable, isMountedRef, setCheckoutState],
    );

    const handleCreateTransactionDataError = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err: any) => {
            const { transactionData } = createTransactionState;
            if (!isMountedRef.current || !transactionData) {
                return;
            }

            console.log(err);

            // TODO: add error handler
        },
        [createTransactionState, isMountedRef],
    );

    useEffect(() => {
        // is mounted check..
        if (
            !isMountedRef.current ||
            apiCallRef.current ||
            !createTransactionState.createTransactionInProgress ||
            !createTransactionState.transactionData
        ) {
            return;
        }

        apiCallRef.current = true;
        const transactionData = createTransactionState.transactionData;
        const createTransactionParams = getCreateTransactionParams({
            transactionData,
            isJuspaySdkAvailable,
        });

        createTransaction(apiHost, createTransactionParams)
            .then(handleCreateTransactionData)
            .catch(handleCreateTransactionDataError)
            .catch(captureError)
            .then(() => {
                if (isMountedRef.current) {
                    apiCallRef.current = false;
                    setCreateTransactionState({ transactionData, createTransactionInProgress: false });
                }
            });
    }, [
        apiHost,
        paymentReturnUrl,
        handleCreateTransactionDataError,
        handleCreateTransactionData,
        isMountedRef,
        createTransactionState.createTransactionInProgress,
        createTransactionState.transactionData,
        isJuspaySdkAvailable,
        createTransaction,
    ]);

    return [createTransactionState, doCreateTransaction];
};

export default useCreateTransaction;
